import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SpecificationEditor, { cleanSpecifications } from '@/components/SpecificationEditor';
import { SpecificationKey } from '@/types/types';
import ModalPortal from '@/components/ModalPortal';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Package, Search, Edit2, Loader2, Save,
    Zap, Cpu, HardDrive, Settings, AlertCircle,
    Hash, Layers, Bookmark,
    Laptop, FileText, History, X, ArrowUpRight, ArrowDownLeft,
    Clock, User, MapPin, FileCheck, Building2, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/api/axios';
import { Item, Brand, ItemModel } from '@/types/types';
import { useAuth } from '@/auth/AuthContext';

interface SpringPageResponse<T> {
    content: T[];
    last?: boolean;
    totalElements?: number;
    page?: {
        size: number;
        number: number;
        totalElements: number;
        totalPages: number;
    };
}

// Interface para as Movimentações do Histórico
interface StockMovement {
    id: string;
    movementType: 'INPUT' | 'OUTPUT' | 'INSTALLATION' | 'TRANSFER' | 'ADJUSTMENT';
    quantity: number;
    serialNumber?: string;
    patrimony?: string;
    originLocalName?: string;
    destinationLocalName?: string;
    justification?: string;
    createdAt: string;
    responsibleUser: string;
    receiptNumber?: string;
}

const STATUS_CONFIG = {
    STOCK: { label: 'Em Estoque', color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', glow: 'bg-emerald-500/25' },
    INSTALLED: { label: 'Instalado', color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20', glow: 'bg-blue-500/25' },
    ACTIVE: { label: 'Ativo', color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', glow: 'bg-cyan-500/25' },
    MAINTENANCE: { label: 'Manutenção', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', glow: 'bg-amber-500/25' },
    EXTERNAL_REPAIR: { label: 'Maint. Externa', color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20', glow: 'bg-orange-500/25' },
    DISCARDED: { label: 'Baixa/Descarte', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20', glow: 'bg-red-500/25' }
};

const MOVEMENT_TYPE_CONFIG = {
    INPUT: { label: 'Entrada / Cadastramento', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: ArrowDownLeft },
    OUTPUT: { label: 'Saída de Estoque', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: ArrowUpRight },
    INSTALLATION: { label: 'Instalação em PC', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: Cpu },
    TRANSFER: { label: 'Transferência entre Locais', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: MapPin },
    ADJUSTMENT: { label: 'Ajuste de Saldo', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Settings }
};

// Mapeamento dinâmico de ícones com base no Tipo do Item para diferenciar Hardware de Suprimentos
const getIconForType = (type: string = '') => {
    const t = type.toLowerCase();
    if (t.includes('sulfite') || t.includes('papel') || t.includes('suprimento') || t.includes('escritorio')) {
        return <FileText size={15} />;
    }
    if (t.includes('ssd') || t.includes('hd') || t.includes('disco') || t.includes('armazenamento')) {
        return <HardDrive size={15} />;
    }
    return <Cpu size={15} />;
};

const CoreStockManagerPage: React.FC = () => {
    const { activeSecretary, user, setActiveSecretary } = useAuth();
    const canManageStock=['ADMIN','TECHNICAL_ADMIN','TECHNICAL'].includes(user?.role || '');
    const canReceiveStock=['ADMIN','TECHNICAL_ADMIN'].includes(user?.role || '');
    const navigate=useNavigate();const route=useLocation();const {id:editId}=useParams<{id:string}>();
    const isForm=route.pathname.endsWith('/new') || !!editId;
    const [specificationKeys,setSpecificationKeys]=useState<SpecificationKey[]>([]);
    useEffect(()=>{let alive=true;(async()=>{try{const keys:SpecificationKey[]=[];let page=0,total=1;do{const r=await api.get('/specification-Key/all',{params:{page,size:100}});keys.push(...r.data.content);total=(r.data.page || r.data).totalPages || 1;page++;}while(page<total);if(alive)setSpecificationKeys(keys);}catch{if(alive)setEntryMessage('Não foi possível carregar as chaves de especificação.');}})();return()=>{alive=false;};},[]);
    const [items, setItems] = useState<Item[]>([]);
    const [itemPage, setItemPage] = useState(0);
    const [itemHasMore, setItemHasMore] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsError, setItemsError] = useState('');
    const [loadingMoreItems, setLoadingMoreItems] = useState(false);

    const listBottomRef = useRef<HTMLDivElement>(null);

    const [brands, setBrands] = useState<Brand[]>([]);
    const [models, setModels] = useState<ItemModel[]>([]);
    const [itemTypes, setItemTypes] = useState<{ id: string, name: string, isUnique?: boolean }[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
    const [loadingModels, setLoadingModels] = useState<boolean>(false);
    const [loadingTypes, setLoadingTypes] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // ESTADOS PARA O MODAL DE HISTÓRICO DE MOVIMENTAÇÕES
    const [selectedHistoryItem, setSelectedHistoryItem] = useState<Item | null>(null);
    const [movementsHistory, setMovementsHistory] = useState<StockMovement[]>([]);
    const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

    const listVersion = useRef(0);
    const [inventoryStatus, setInventoryStatus] = useState('STOCK');
    const [discardItem, setDiscardItem] = useState<Item | null>(null); const [discardBusy, setDiscardBusy] = useState(false);
    const [catalogCategory, setCatalogCategory] = useState('SUPPLY');
    const [reference, setReference] = useState<'type' | 'brand' | 'model' | null>(null);
    const [referenceName, setReferenceName] = useState(''); const [referenceUnique, setReferenceUnique] = useState(false);
    const [referenceBusy, setReferenceBusy] = useState(false); const [referenceError, setReferenceError] = useState('');
    const [entryMessage, setEntryMessage] = useState(''); const [entrySaving, setEntrySaving] = useState(false);
    const [historyStart, setHistoryStart] = useState(''); const [historyEnd, setHistoryEnd] = useState('');
    const openReference = (kind: 'type' | 'brand' | 'model') => { setReference(kind); setReferenceName(''); setReferenceUnique(false); setReferenceError(''); };
    const saveReference = async (event: React.FormEvent) => {
        event.preventDefault(); if (!reference || referenceBusy || !referenceName.trim()) return;
        setReferenceBusy(true); setReferenceError('');
        try {
            const path = reference === 'type' ? '/item-type' : reference === 'brand' ? '/brand' : '/item-model';
            const payload = reference === 'type' ? { name: referenceName.trim(), affectsRanking: false, isUnique: referenceUnique, category: catalogCategory }
                : reference === 'brand' ? { name: referenceName.trim() } : { name: referenceName.trim(), brandId: formData.brandId, itemTypeId: formData.itemTypeId, ranking: 0 };
            const r = await api.post(path, payload);
            if (reference === 'type') { setItemTypes(old => [...old, r.data]); setFormData(old => ({ ...old, itemTypeId: r.data.id, brandId: '', modelId: '' })); }
            if (reference === 'brand') { setBrands(old => [...old, r.data]); setFormData(old => ({ ...old, brandId: r.data.id, modelId: '' })); }
            if (reference === 'model') { setModels(old => [...old, r.data]); setFormData(old => ({ ...old, modelId: r.data.id })); }
            setReference(null);
        } catch (e: any) { setReferenceError(Object.values(e.response?.data?.violations || {}).join(' ') || e.response?.data?.message || 'Não foi possível cadastrar.'); }
        finally { setReferenceBusy(false); }
    };
    const [formData, setFormData] = useState({
        id: '',
        itemTypeId: '', // ID do ItemTypeEntity
        brandId: '',         // ID do BrandEntity
        modelId: '',         // ID do ItemModelEntity
        quantity: 1,
        serialNumber: '',
        patrimony: '',
        status: 'STOCK',
        specifications: {} as Record<string,string>
    });

    useEffect(()=>{if(!editId){setFormData({id:'',itemTypeId:'',brandId:'',modelId:'',quantity:1,serialNumber:'',patrimony:'',status:'STOCK',specifications:{}});return;}let alive=true;api.get(`/items/${editId}`).then(r=>{if(!alive)return;const item:Item=r.data;setCatalogCategory(item.itemType?.category || 'COMPONENT');setFormData({id:item.id,itemTypeId:item.itemType.id,brandId:item.brand.id,modelId:item.model.id,quantity:item.quantity || 1,serialNumber:item.serialNumber || '',patrimony:item.patrimony || '',status:item.status,specifications:Object.fromEntries((item.specifications || []).map(spec=>[spec.key.id,spec.value]))});}).catch(()=>setEntryMessage('Não foi possível carregar o item para edição.'));return()=>{alive=false;};},[editId]);
    // Carrega itens cadastrados no estoque central
    const loadItems = useCallback(async (pageNum: number, clear: boolean = false, searchStr: string = '') => {
        if (pageNum > 0) setLoadingMoreItems(true);
        else setLoading(true);
        setItemsError('');
        const version = clear ? ++listVersion.current : listVersion.current;
        try {
            const resItems = await api.get<SpringPageResponse<Item>>('/items/all', {
                params: {
                    search: searchStr,
                    status: inventoryStatus, category: catalogCategory,
                    page: pageNum,
                    size: 10
                }
            });

            if(version !== listVersion.current) return;
            const itemData = resItems.data.content ?? [];
            const pageMetadata = resItems.data.page;
            const isLastPage = resItems.data.last
                ?? (pageMetadata ? pageMetadata.number + 1 >= pageMetadata.totalPages : true);

            setItems(prev => [...new Map((clear ? itemData : [...prev,...itemData]).map((i: Item) => [i.id,i])).values()] as Item[]);
            setItemHasMore(!isLastPage);
            setTotalItems(resItems.data.totalElements
                ?? pageMetadata?.totalElements
                ?? (Array.isArray(itemData) ? itemData.length : 0));
        } catch (err: any) {
            if(version !== listVersion.current) return;
            console.error("Erro ao carregar itens CPD:", err);
            setItemsError(err?.response?.data?.message
                || err?.response?.data?.detail
                || 'Não foi possível carregar o estoque desta equipe.');
            if (clear) {
                setItems([]);
                setTotalItems(0);
            }
        } finally {
            if(version === listVersion.current) { setLoading(false); setLoadingMoreItems(false); }

        }
    }, [catalogCategory,inventoryStatus]);

    // Carrega tipos de equipamentos / itens (ItemTypeEntity)
    const loadTypes = useCallback(async () => {
        setLoadingTypes(true);
        try {
            const list: { id: string; name: string; isUnique?: boolean }[] = []; let page = 0; let pages = 1;
            do { const r = await api.get('/item-type/all', { params: { page, size: 100, category: catalogCategory } }); list.push(...r.data.content); pages = (r.data.page || r.data).totalPages; page++; } while (page < pages);
            setItemTypes(list);
        } catch (err) {
            console.error("Erro ao buscar tipos de equipamentos:", err);
        } finally {
            setLoadingTypes(false);
        }
    }, [catalogCategory]);

    useEffect(() => {
        loadTypes();
    }, [loadTypes]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setItemPage(0);
            setItemHasMore(true);
            loadItems(0, true, searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [activeSecretary?.id, searchTerm, loadItems]);

    // Observer do Infinite Scroll
    useEffect(() => {
        if (loading || !itemHasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMoreItems) {
                    const nextPage = itemPage + 1;
                    setItemPage(nextPage);
                    loadItems(nextPage, false, searchTerm);
                }
            },
            { threshold: 0.1 }
        );

        const currentRef = listBottomRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [itemPage, itemHasMore, loading, loadingMoreItems, searchTerm, loadItems]);

    const loadBrands = useCallback(async () => {
        setLoadingBrands(true);
        try {
            const values: Brand[] = []; let page = 0; let pages = 1;
            do { const r = await api.get('/brand/all', { params: { page, size: 100, sort: 'name,asc' } }); values.push(...r.data.content); pages = (r.data.page || r.data).totalPages; page++; } while(page < pages);
            setBrands(old => [...new Map([...values, ...old].map(b => [b.id,b])).values()]);
        } catch { setEntryMessage('Não foi possível carregar as marcas. Tente novamente.'); }
        finally { setLoadingBrands(false); }
    }, []);
    useEffect(() => { void loadBrands(); }, [loadBrands]);
    // CASCATA: Dispara quando o Tipo de Item E a Marca estão selecionados
    useEffect(() => {
        if (!formData.brandId || !formData.itemTypeId) {
            setModels([]);
            return;
        }

        const controller = new AbortController();
        setModels([]);
        const fetchModelsByBrandAndType = async () => {
            setLoadingModels(true);
            try {
                // Endpoint preparado para retornar os modelos filtrando por tipo e marca correspondente no banco
                const response = await api.get(`/item-model/brand/${formData.brandId}`, {
                    params: { itemTypeId: formData.itemTypeId }, signal: controller.signal
                });
                if(!controller.signal.aborted) setModels(old => [...new Map([...(Array.isArray(response.data) ? response.data : []), ...old].map(m => [m.id,m])).values()]);
            } catch (err) {
                if(controller.signal.aborted) return;
                console.error("Erro ao buscar modelos filtrados no CPD:", err);
                setModels([]);
            } finally {
                if(!controller.signal.aborted) setLoadingModels(false);
            }
        };
        fetchModelsByBrandAndType();
        return () => controller.abort();
    }, [formData.brandId, formData.itemTypeId]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (entrySaving) return;
        if(editId && formData.id!==editId){setEntryMessage('Aguarde o carregamento do item.');return;}
        if (!formData.modelId || formData.quantity < 1) { setEntryMessage('Selecione tipo, marca e modelo e informe uma quantidade maior que zero.'); return; }
        setEntrySaving(true); setEntryMessage('');
        try {
            if (formData.id) await api.put(`/items/${formData.id}`, {...formData,specifications:cleanSpecifications(formData.specifications)});
            else await api.post('/items/entries', { ...formData, specifications:cleanSpecifications(formData.specifications), status: 'STOCK' });

            setEntryMessage(formData.id ? 'Item atualizado.' : 'Entrada registrada. O saldo já está disponível na Saída de materiais.');
            navigate('/core-stock-manager');
            setItemPage(0);
            setInventoryStatus('STOCK');
            if(inventoryStatus === 'STOCK') loadItems(0, true, searchTerm);

            setFormData({
                ...formData,
                id: '',
                itemTypeId: '',
                brandId: '',
                modelId: '',
                serialNumber: '',
                patrimony: '',
                quantity: 1,
                specifications: {}
            });
        } catch (err: any) {
            setEntryMessage(Object.values(err.response?.data?.violations || {}).join(' ') || err.response?.data?.message || 'Não foi possível registrar a entrada.');
        } finally { setEntrySaving(false); }
    };

    const handleOpenHistory = async (item: Item) => {
        setSelectedHistoryItem(item);
        setLoadingHistory(true);
        try {
            const res = await api.get(`/stock/${item.id}/movements`);
            setMovementsHistory(Array.isArray(res.data) ? res.data : res.data.content || []);
        } catch (err) {
            console.error("Erro ao buscar histórico de movimentações:", err);
            setMovementsHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const selectedBrandName = brands.find(b => b.id === formData.brandId)?.name || "Selecione a Marca...";
    const availableSecretaries = (user?.itTeams ?? []).flatMap(team => {
        const secretary = team.secretaries.find(s => s.id === activeSecretary?.id) ?? team.secretaries[0];
        return secretary ? [secretary] : [];
    });

    const handleSecretaryChange = (secretaryId: string) => {
        const secretary = availableSecretaries.find(option => option.id === secretaryId);
        if (!secretary || secretary.id === activeSecretary?.id) return;

        // Limpa imediatamente os itens da secretaria anterior. O interceptor
        // da API lerá o novo contexto salvo pelo AuthContext na próxima busca.
        setItems([]);
        setTotalItems(0);
        setItemPage(0);
        setItemHasMore(true);
        setActiveSecretary(secretary);
    };

    return (
        <div className="stock-workspace page-content">
            {discardItem && (<ModalPortal><div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"><section role="dialog" aria-modal="true" aria-label="Baixa de item" className="stock-workspace ui-card w-full max-w-lg space-y-4"><h2 className="font-semibold">Dar baixa / descarte</h2><p>Confirma a baixa de {discardItem.model?.name}? A movimentação ficará no histórico.</p><div className="flex justify-end gap-3"><button disabled={discardBusy} className="ui-button-secondary" onClick={() => setDiscardItem(null)}>Cancelar</button><button disabled={discardBusy} className="ui-button-primary" onClick={async () => { setDiscardBusy(true); try { await api.delete(`/items/${discardItem.id}`); setDiscardItem(null); await loadItems(0,true,searchTerm); setEntryMessage('Baixa registrada no histórico.'); } catch(e: any) { setEntryMessage(e.response?.data?.message || 'Não foi possível registrar a baixa.'); } finally { setDiscardBusy(false); } }}>Confirmar baixa</button></div></section></div></ModalPortal>)}
            {reference && (<ModalPortal><div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"><form role="dialog" aria-modal="true" aria-label="Cadastro rápido de material" className="stock-workspace ui-card w-full max-w-lg space-y-4" onSubmit={saveReference}><h2 className="text-lg font-semibold">Cadastrar {reference === 'type' ? 'tipo de material' : reference === 'brand' ? 'marca' : 'modelo'}</h2><label className="block text-sm">Nome<input autoFocus required maxLength={reference === 'type' ? 100 : 150} className="form-field mt-1" value={referenceName} onChange={e => setReferenceName(e.target.value)} /></label>{reference === 'type' && catalogCategory === 'COMPONENT' && <label className="flex gap-3 text-sm"><input type="checkbox" checked={referenceUnique} onChange={e => setReferenceUnique(e.target.checked)} />Único por equipamento (ex.: processador)</label>}{reference === 'model' && <p className="text-sm text-slate-400">Tipo: {itemTypes.find(t => t.id === formData.itemTypeId)?.name} · Marca: {selectedBrandName}</p>}{referenceError && <p role="alert" className="feedback-error">{referenceError}</p>}<div className="flex justify-end gap-3"><button type="button" disabled={referenceBusy} className="ui-button-secondary" onClick={() => setReference(null)}>Cancelar</button><button disabled={referenceBusy} className="ui-button-primary">{referenceBusy ? 'Salvando...' : 'Salvar e selecionar'}</button></div></form></div></ModalPortal>)}
            <div className="space-y-6">
                <div className="ui-card flex flex-wrap items-center justify-between gap-4"><h1 className="text-lg font-semibold">{isForm ? (editId ? 'Editar item' : 'Nova entrada') : 'Estoque'}</h1>{(isForm || canReceiveStock) && <button className={isForm ? 'ui-button-secondary' : 'ui-button-primary'} onClick={()=>navigate(isForm ? '/core-stock-manager' : '/core-stock-manager/new')}>{isForm ? 'Voltar à listagem' : '+ Novo'}</button>}</div>
                {entryMessage && <p role="status">{entryMessage}</p>}
                <label className="block text-sm">Categoria de estoque<select className="form-field mt-1" value={catalogCategory} onChange={e=>{setCatalogCategory(e.target.value);setInventoryStatus('STOCK');setFormData(old=>({...old,itemTypeId:'',brandId:'',modelId:'',specifications:{}}));}}><option value="SUPPLY">Materiais e suprimentos</option><option value="COMPONENT">Peças e bens reutilizáveis</option></select></label>
                {isForm && <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex w-full flex-col justify-end gap-3 sm:flex-row"
                >
                        <button
                            onClick={() => {if(editId){navigate('/core-stock-manager');return;}setFormData({...formData, id: '', itemTypeId: '', brandId: '', modelId: '', serialNumber: '', patrimony: '',specifications:{},quantity:1});}}
                            className="ui-button-secondary"
                        >
                            {formData.id ? 'Cancelar edição' : 'Limpar campos'}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={entrySaving || (!!editId && formData.id!==editId)}
                            className="ui-button-primary"
                        >
                            <Save size={16}/> {entrySaving ? 'Registrando...' : formData.id ? 'Atualizar item' : 'Registrar entrada'}
                        </button>
                </motion.div>}

                <div className="grid grid-cols-12 gap-8">

                    {/* FORMULÁRIO COM FLUXO EM CASCATA (LADO ESQUERDO) */}
                    {isForm && <div className="col-span-12 space-y-6">
                        <div className="ui-card ui-card-hover p-8">
                            <div className="absolute -top-10 -right-10 p-8 opacity-[0.03] text-white pointer-events-none">
                                <Settings size={280} />
                            </div>

                            <h2 className="text-xs font-black text-white uppercase tracking-[0.2em] mb-8 flex items-center gap-2 relative z-10">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                Entrada de materiais e componentes
                            </h2>

                            <p className="mb-4 text-sm text-slate-400">Papel, toner, caneta, caderno ou peça: selecione o tipo, a marca e o modelo. Cadastre o que faltar aqui, sem sair da entrada.</p>
                            <div className="mb-4 flex flex-wrap gap-2"><button type="button" className="ui-button-secondary" onClick={() => openReference('type')}>+ Tipo de material</button><button type="button" disabled={!formData.itemTypeId} className="ui-button-secondary" onClick={() => openReference('brand')}>+ Marca</button><button type="button" disabled={!formData.itemTypeId || !formData.brandId} className="ui-button-secondary" onClick={() => openReference('model')}>+ Modelo</button></div>
                            <form className="space-y-5 relative z-10">

                                <p className="text-sm text-slate-400">{catalogCategory === 'SUPPLY' ? 'Material de consumo: saldo controlado por quantidade.' : 'Bem reutilizável: cada unidade mantém seu registro, destino e histórico. Para informar série ou patrimônio, cadastre uma unidade por vez.'}</p>
                                {/* PASSO 1: Tipo de Item (Sulfite, Processador, Memória) */}
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                        <Laptop size={10}/> 1. Tipo de Item
                                    </label>
                                    <div className="relative">
                                        <Package size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                                        <select
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-4 pl-11 outline-none focus:ring-1 focus:ring-blue-500 text-white text-sm appearance-none"
                                            aria-label="Tipo de item" value={formData.itemTypeId}
                                            onChange={e => setFormData({
                                                ...formData,
                                                itemTypeId: e.target.value,

                                                brandId: '', // Reseta os passos seguintes se mudar o tipo
                                                modelId: ''
                                            })}
                                        >
                                            <option value="">{loadingTypes ? 'Carregando categorias...' : 'Selecione o Tipo...'}</option>
                                            {itemTypes.map(t => <option key={t.id} value={t.id}>{t.name.toUpperCase()}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* PASSO 2: Fabricante/Marca (Chamex, Kingston, Intel) */}
                                <div className="space-y-1 relative">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                        <Bookmark size={10}/> 2. Fabricante
                                    </label>

                                    <select aria-label="Marca" className="form-field" disabled={!formData.itemTypeId || loadingBrands} value={formData.brandId} onChange={e => setFormData({ ...formData, brandId: e.target.value, modelId: '' })}>
                                        <option value="">{loadingBrands ? 'Carregando marcas...' : 'Selecione a marca...'}</option>{brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>

                                {/* PASSO 3: Modelo filtrado (Report A4 75g) */}
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                        <Layers size={10}/> 3. Modelo do material ou componente
                                    </label>
                                    <div className="relative">
                                        <Cpu size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                                        <select
                                            disabled={!formData.brandId || !formData.itemTypeId}
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-4 pl-11 outline-none focus:ring-1 focus:ring-blue-500 text-white text-sm appearance-none disabled:opacity-20 transition-opacity"
                                            aria-label="Modelo" value={formData.modelId}
                                            onChange={e => setFormData({...formData, modelId: e.target.value})}
                                        >
                                            <option value="">
                                                {loadingModels ? 'Buscando modelos compatíveis...' :
                                                    !formData.brandId ? 'Aguardando Marca...' : 'Selecione o Modelo...'}
                                            </option>
                                            {models.map(m => <option key={m.id} value={m.id}>{m.name.toUpperCase()}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Patrimônio e Serial */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                            <Hash size={10}/> Patrimônio
                                        </label>
                                        <input
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-4 outline-none focus:ring-1 focus:ring-blue-500 text-white font-mono text-sm uppercase placeholder:text-slate-800"
                                            placeholder="00000"
                                            value={formData.patrimony}
                                            onChange={e => setFormData({...formData, patrimony: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                            <Zap size={10}/> Nº de Série
                                        </label>
                                        <input
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-4 outline-none focus:ring-1 focus:ring-blue-500 text-white font-mono text-sm uppercase placeholder:text-slate-800"
                                            placeholder="SN-..."
                                            value={formData.serialNumber}
                                            onChange={e => setFormData({...formData, serialNumber: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <p className="text-sm text-slate-400">Situação da entrada: <strong>Em estoque</strong>. Instalação, saída e baixa são registradas nas ações próprias.</p>
                                <div className="grid grid-cols-1 gap-4"><div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-500 uppercase ml-2 flex items-center gap-1">
                                            <Package size={10}/> Qtd
                                        </label>
                                        <input
                                            type="number"
                                            className="w-full bg-[#0b0f1a] border border-white/5 rounded-2xl p-4 text-white text-sm outline-none"
                                            disabled={!!formData.id} aria-label="Quantidade" value={formData.quantity}
                                            onChange={e => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                                        />
                                    </div>
                                </div>
                                <SpecificationEditor title="Especificações do item" keys={specificationKeys} value={formData.specifications} onChange={specifications=>setFormData(old=>({...old,specifications}))}/>
                            </form>
                        </div>
                    </div>}

                    {/* LISTAGEM DE ESTOQUE LINEAR - INFINITE SCROLL (LADO DIREITO) */}
                    <div className={`col-span-12 space-y-6 ${isForm ? 'hidden' : ''}`}><div className="ui-card space-y-3"><h2 className="font-semibold">{catalogCategory === 'SUPPLY' ? 'Materiais de consumo' : 'Peças e bens reutilizáveis'}</h2><div className="flex flex-wrap gap-2"><button className={inventoryStatus === 'STOCK' ? 'ui-button-primary' : 'ui-button-secondary'} onClick={() => { if(inventoryStatus === 'STOCK') return; listVersion.current++; setItems([]); setInventoryStatus('STOCK'); }}>Em estoque</button>{catalogCategory === 'COMPONENT' && <button className={inventoryStatus === 'ACTIVE' ? 'ui-button-primary' : 'ui-button-secondary'} onClick={() => { if(inventoryStatus === 'ACTIVE') return; listVersion.current++; setItems([]); setInventoryStatus('ACTIVE'); }}>Em uso nos locais</button>}</div></div>
                        <div className="ui-card ui-card-hover flex h-[650px] flex-col p-8">

                            {/* CABEÇALHO */}
                            <div className="mb-4 flex shrink-0 flex-col gap-3 rounded-2xl border border-white/5 bg-white/[0.03] p-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-400">
                                        <Building2 size={16} />
                                    </span>
                                    <div className="min-w-0">
                                        <span className="block text-[8px] font-black uppercase tracking-widest text-slate-600">Estoque da equipe de TI</span>
                                        {availableSecretaries.length > 1 ? (
                                            <select
                                                aria-label="Equipe do estoque"
                                                className="max-w-full bg-transparent text-xs font-bold text-slate-300 outline-none"
                                                value={activeSecretary?.id ?? ''}
                                                onChange={event => handleSecretaryChange(event.target.value)}
                                            >
                                                {availableSecretaries.map(secretary => (
                                                    <option key={secretary.id} value={secretary.id}>{secretary.itTeam?.name || secretary.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <strong className="block truncate text-xs text-slate-300">{activeSecretary?.itTeam?.name || 'Nenhuma equipe selecionada'}</strong>
                                        )}
                                    </div>
                                </div>
                                <span className="shrink-0 rounded-xl border border-white/5 bg-black/10 px-3 py-2 text-[9px] font-black uppercase tracking-wider text-slate-500">
                                    {totalItems} {totalItems === 1 ? 'item disponível' : 'itens disponíveis'}
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 shrink-0">
                                <div>
                                    <h2 className="text-xl font-black text-white italic">INVENTORY<span className="text-blue-500">_LIST</span></h2>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                                    <input
                                        className="w-full bg-[#0b0f1a] border border-white/5 rounded-xl pl-10 pr-4 py-2 text-xs outline-none focus:border-blue-500/50 transition-all"
                                        placeholder="Tipo, marca, modelo, DDR4, 8GB..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* LISTA LINEAR ROLÁVEL */}
                            <div className="flex-1 overflow-y-auto pr-2 space-y-2.5 custom-scrollbar">
                                {loading && items.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full gap-4 opacity-30">
                                        <Loader2 className="animate-spin text-blue-500" size={32} />
                                        <span className="text-[10px] font-black uppercase tracking-[0.4em]">Carregando Itens...</span>
                                    </div>
                                ) : itemsError && items.length === 0 ? (
                                    <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-red-500/20 bg-red-500/5 p-6 text-center text-red-300">
                                        <AlertCircle size={36} />
                                        <p className="max-w-sm text-xs font-bold leading-6">{itemsError}</p>
                                    </div>
                                ) : items.length > 0 ? (
                                    <>
                                        <div className="space-y-2">
                                            {items.map((item, idx) => {
                                                const status = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.STOCK;

                                                return (
                                                    <motion.div
                                                        initial={{ opacity: 0, x: 20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.25, delay: Math.min(idx * 0.03, 0.2) }}
                                                        key={item.id}
                                                        className="group p-4 bg-[#0b0f1a] border border-white/5 hover:border-blue-500/30 rounded-2xl transition-all flex items-center justify-between gap-4 relative overflow-hidden"
                                                    >
                                                        {/* Identificador com Ícone e Marca/Modelo (Lado Esquerdo) */}
                                                        <div className="flex items-center gap-3 min-w-[190px] max-w-[250px]">
                                                            <div className="relative flex items-center justify-center w-10 h-10 shrink-0">
                                                                <div className={`absolute w-8 h-8 rounded-full ${status.glow} blur-md opacity-40 group-hover:scale-125 transition-transform duration-300`} />
                                                                <div className={`relative w-9 h-9 rounded-xl bg-[#161e2d] border ${status.border} text-blue-400 flex items-center justify-center z-10`}>
                                                                    {getIconForType(item.itemType.name)}
                                                                </div>
                                                            </div>
                                                            <div className="truncate space-y-0.5">
                                                                <div className="flex items-center gap-1.5">
                                                                    <h3 className="pr-4 text-[12px] text-slate-500 font-bold uppercase">{item.itemType?.name}</h3>

                                                                    {/* Badge dinâmico de categoria */}
                                                                    {item.brand && (
                                                                        <span className="p-2 px-1.5 py-0.5 bg-blue-500/5 border border-blue-500/10 text-[7px] font-bold uppercase rounded text-slate-400 tracking-wider">
                                                                            {item.brand.name}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <h2 className="text-xs font-black text-white uppercase truncate">{item.model?.name}</h2>
                                                                {!item.consumable && <p className="text-xs text-slate-400">{item.patrimony ? `Patrimônio: ${item.patrimony}` : item.serialNumber ? `Série: ${item.serialNumber}` : `Registro: ${item.id.slice(0,8)}`}</p>}
                                                                {item.local && <p className="text-xs text-slate-400">{item.local.unit?.name} / {item.local.name}</p>}
                                                            </div>
                                                        </div>

                                                        {/* Patrimônio e Serial (Centro-Esquerdo) */}
                                                        <div className="hidden sm:grid grid-cols-2 gap-4 text-left min-w-[150px]">
                                                            <div>
                                                                <span className="text-[7px] font-black text-slate-600 uppercase block tracking-wider">Patrimônio</span>
                                                                <span className="text-[9px] text-slate-300 font-mono truncate block">{item.patrimony || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-[7px] font-black text-slate-600 uppercase block tracking-wider">Série</span>
                                                                <span className="text-[9px] text-blue-400/80 font-mono truncate block">{item.serialNumber || 'N/A'}</span>
                                                            </div>
                                                        </div>

                                                        {/* Status e Quantidade (Centro-Direito) */}
                                                        <div className="flex items-center gap-4 shrink-0">
                                                            <span className={`p-2 px-2.5 py-1 rounded-full border ${status.border} ${status.bg} ${status.color} text-[8px] font-black uppercase tracking-wider`}>
                                                                {status.label}
                                                            </span>

                                                            <div className="text-right min-w-[40px]">
                                                                <span className="text-xs font-black text-blue-500">{item.quantity}</span>
                                                                <span className="text-[9px] font-black text-slate-600 uppercase block italic">unid</span>
                                                            </div>
                                                        </div>

                                                        {/* BOTÕES DE AÇÃO: HISTÓRICO & EDIÇÃO */}
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => handleOpenHistory(item)}
                                                                title="Ver histórico de movimentações"
                                                                className="p-2 bg-slate-800/80 hover:bg-blue-600 text-slate-400 hover:text-white rounded-xl transition-all border border-white/5 hover:border-blue-500/30"
                                                            >
                                                                <History size={14} />
                                                            </button>
                                                            {canManageStock && item.status === 'STOCK' && <button
                                                                onClick={() => {
                                                                    setCatalogCategory(item.itemType.category || 'COMPONENT');
                                                                    setFormData({
                                                                        id: item.id,
                                                                        itemTypeId: item.itemType.id || '',
                                                                        brandId: item.brand.id || '',
                                                                        modelId: item.model.id || '',
                                                                        quantity: item.quantity || 1,

                                                                        serialNumber: item.serialNumber || '',
                                                                        patrimony: item.patrimony || '',
                                                                        status: item.status || 'STOCK',
                                                                        specifications: Object.fromEntries((item.specifications || []).map(spec=>[spec.key.id,spec.value]))
                                                                    });
                                                                    navigate(`/core-stock-manager/${item.id}/edit`);
                                                                }}
                                                                title="Editar item"
                                                                className="p-2 bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white rounded-xl transition-all border border-white/5"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>}
                                                            {canManageStock && <button type="button" title="Dar baixa / descarte" aria-label={`Dar baixa de ${item.model?.name}`} onClick={() => setDiscardItem(item)} className="ui-button-secondary"><Trash2 size={14} /></button>}
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>

                                        {/* Detecção para gatilho de página */}
                                        <div ref={listBottomRef} className="h-10 flex items-center justify-center py-2">
                                            {loadingMoreItems && (
                                                <Loader2 size={18} className="animate-spin text-blue-500 opacity-60" />
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                                        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl border border-amber-500/20 bg-amber-500/10 text-amber-400">
                                            <Package size={30} />
                                        </span>
                                        <p className="text-sm font-black text-slate-300">Nenhum item em estoque nesta equipe</p>
                                        <p className="mt-2 max-w-md text-[10px] leading-5 text-slate-500">
                                            A lista exibe o estoque exclusivo da equipe ativa, compartilhado entre as secretarias atendidas por ela.
                                            {availableSecretaries.length > 1 && ' Selecione outra equipe acima para consultar o estoque correspondente.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* MODAL / POPUP DE HISTÓRICO DE MOVIMENTAÇÕES (AUDITORIA) */}
            <AnimatePresence>
                {selectedHistoryItem && (
                    <ModalPortal><div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-[#161e2d] border border-white/10 rounded-[2.5rem] w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl relative"
                        >
                            <div className="grid gap-3 p-4 md:grid-cols-2"><label className="text-sm">Movimentações a partir de<input type="date" className="form-field" value={historyStart} max={historyEnd || undefined} onChange={e => setHistoryStart(e.target.value)} /></label><label className="text-sm">Até<input type="date" className="form-field" value={historyEnd} min={historyStart || undefined} onChange={e => setHistoryEnd(e.target.value)} /></label></div>
                            {/* CABEÇALHO DO MODAL */}
                            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-[#0b0f1a]/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-blue-600/20 border border-blue-500/20 text-blue-400 rounded-xl">
                                        <History size={18} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-[10px] font-black text-blue-500 uppercase tracking-widest">
                                            Linha do Tempo / Auditoria
                                        </div>
                                        <h2 className="text-base font-black text-white uppercase italic">
                                            {selectedHistoryItem.model?.name} <span className="text-slate-500">({selectedHistoryItem.brand?.name})</span>
                                        </h2>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedHistoryItem(null)}
                                    className="p-2 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700 rounded-xl transition-all"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* SUB-HEADER COM DADOS DO ITEM */}
                            <div className="px-6 py-3 bg-[#0b0f1a] border-b border-white/5 flex flex-wrap items-center justify-between gap-4 text-xs font-mono">
                                <div className="flex items-center gap-4 text-slate-400">
                                    <span>PAT: <strong className="text-white">{selectedHistoryItem.patrimony || 'N/A'}</strong></span>
                                    <span>SN: <strong className="text-white">{selectedHistoryItem.serialNumber || 'N/A'}</strong></span>
                                </div>
                                <div className="text-slate-500">
                                    SALDO ATUAL: <strong className="text-blue-400 font-bold">{selectedHistoryItem.quantity} Un.</strong>
                                </div>
                            </div>

                            {(selectedHistoryItem.specifications || []).length>0 && <dl className="grid gap-3 p-6 sm:grid-cols-2">{selectedHistoryItem.specifications.map(spec=><div key={spec.key.id}><dt className="text-xs text-slate-400">{spec.key.displayName || spec.key.keyName}</dt><dd className="text-sm font-semibold">{spec.value} {spec.key.unit || ''}</dd></div>)}</dl>}
                            {/* CONTEÚDO / TIMELINE */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                {loadingHistory ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500">
                                        <Loader2 size={24} className="animate-spin text-blue-500" />
                                        <span className="text-xs font-black uppercase tracking-widest">Buscando histórico de movimentações...</span>
                                    </div>
                                ) : movementsHistory.length > 0 ? (
                                    <div className="relative border-l-2 border-white/10 ml-4 space-y-6 pl-6">
                                        {movementsHistory.filter(m => (!historyStart || m.createdAt.slice(0, 10) >= historyStart) && (!historyEnd || m.createdAt.slice(0, 10) <= historyEnd)).map((mov) => {
                                            const typeConfig = MOVEMENT_TYPE_CONFIG[mov.movementType] || MOVEMENT_TYPE_CONFIG.INPUT;
                                            const Icon = typeConfig.icon;

                                            return (
                                                <div key={mov.id} className="relative group">
                                                    {/* PONTO / ÍCONE DA TIMELINE */}
                                                    <div className={`absolute -left-[35px] top-0 p-1.5 rounded-xl border ${typeConfig.bg} ${typeConfig.border} ${typeConfig.color}`}>
                                                        <Icon size={14} />
                                                    </div>

                                                    {/* CARD DA MOVIMENTAÇÃO */}
                                                    <div className="p-4 bg-[#0b0f1a] border border-white/5 rounded-2xl space-y-2 group-hover:border-white/10 transition-all">
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border ${typeConfig.bg} ${typeConfig.border} ${typeConfig.color}`}>
                                                                {typeConfig.label}
                                                            </span>
                                                            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono">
                                                                <Clock size={11} />
                                                                {new Date(mov.createdAt).toLocaleString('pt-BR')}
                                                            </div>
                                                        </div>

                                                        {/* JUSTIFICATIVA / PARECER */}
                                                        {mov.justification && (
                                                            <p className="text-xs text-slate-300 font-medium leading-relaxed">
                                                                "{mov.justification}"
                                                            </p>
                                                        )}

                                                        {/* DETALHES DE LOCAIS & RECIBO */}
                                                        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/5 text-[11px] text-slate-400">
                                                            <div className="flex items-center gap-2">
                                                                <MapPin size={12} className="text-slate-600" />
                                                                <span>{mov.originLocalName || 'Estoque Central'} → <strong className="text-white">{mov.destinationLocalName || 'CPD / Almoxarifado'}</strong></span>
                                                            </div>

                                                            {mov.receiptNumber && (
                                                                <div className="flex items-center gap-1 text-blue-400 font-mono">
                                                                    <FileCheck size={12} />
                                                                    <span>Termo #{mov.receiptNumber}</span>
                                                                </div>
                                                            )}

                                                            <div className="flex items-center gap-1 text-slate-500">
                                                                <User size={12} />
                                                                <span>{mov.responsibleUser}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-slate-600 gap-2">
                                        <AlertCircle size={28} />
                                        <span className="text-xs font-black uppercase tracking-widest">Nenhuma movimentação registrada para este item</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div></ModalPortal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CoreStockManagerPage;
